import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { parseLiveBytes } from './liveBytesParser'

/**
 * LiveAckButtonControl — PM-1 (Ack-Gated) + PM-4 (Action Lock)
 *
 * Tap → sends a single configured byte once, then self-locks until the device
 * replies with the configured acknowledgement pattern. While locked, the button
 * is visually disabled and pointer events are blocked.
 *
 * Acknowledgement detection — two modes (admin-configurable):
 *
 *   ackMode = 'string' (default)
 *     Admin enters a plain text string (e.g. "Rdone", "OK", "done").
 *     Incoming serial bytes are decoded to a JS string by the worker; we check
 *     str.includes(ackString). Works for ASCII responses.
 *
 *   ackMode = 'bytes'
 *     Admin enters a comma-separated byte list (e.g. "0,82,100,111,110,101,0").
 *     We convert those bytes to a JS string via String.fromCharCode() once, then
 *     check str.includes(thatString) — same pipeline, exact byte match.
 *     Example: "0,82,100,111,110,101,0" → \0Rdone\0
 *
 * PM-4 actionLock flag (per-button):
 *   false (default) — only this button is self-locked while awaiting ack.
 *   true  — also arms the global gate (via onRequestActivate), blocking all
 *            other buttons. onActionComplete() is called on ack so PlayScreen
 *            releases the global lock using the same custom ack pattern.
 *
 * Props:
 *   comp              – full component object from play data
 *   outputStates      – current output state map
 *   onChange          – (mapping, value) callback
 *   resolveImage      – image resolver function
 *   highlightClass    – CSS class string for step highlighting
 *   style             – positioning style object
 *   bypassLock        – if true, fires even while global lock is active
 *   onRequestActivate – () => boolean — parent gate (arms global lock for actionLock=true)
 *   onActionComplete  – () => void — called when ack received; parent releases global lock
 */

/**
 * Build the string pattern to search for in the incoming serial stream.
 *
 * String mode: return the ackString as-is (substring match).
 * Bytes  mode: convert comma-separated byte values to a JS string via
 *              String.fromCharCode so we can still use str.includes().
 *              e.g. "0,82,100,111,110,101,0" → "\x00Rdone\x00"
 */
function buildAckPattern(mode, ackString, ackBytes) {
    if (mode === 'bytes') {
        const nums = (ackBytes || '')
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => !isNaN(n) && n >= 0 && n <= 255)
        if (nums.length > 0) return String.fromCharCode(...nums)
    }
    return (ackString || '').trim() || 'Rdone'
}

function LiveAckButtonControl({
    comp,
    outputStates,
    onChange,
    resolveImage,
    highlightClass,
    style,
    onRequestActivate,
    onActionComplete,
    bypassLock = false,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)

    const selfLockedRef = useRef(false)
    const [isSelfLocked, setIsSelfLocked] = useState(false)

    const liveBytesStr = comp.properties.liveBytes || ''
    const liveBytesArray = useMemo(() => parseLiveBytes(liveBytesStr), [liveBytesStr])

    const ackMode   = comp.properties.ackMode  || 'string'
    const ackString = comp.properties.ackString || ''
    const ackBytes  = comp.properties.ackBytes  || ''

    // Pre-compute the pattern once; re-compute only when config changes.
    const ackPattern = useMemo(
        () => buildAckPattern(ackMode, ackString, ackBytes),
        [ackMode, ackString, ackBytes]
    )

    const releaseSelf = useCallback(() => {
        if (!selfLockedRef.current) return
        selfLockedRef.current = false
        setIsSelfLocked(false)
        onChange(comp.mapping, 0)
        // For actionLock=true buttons, tell PlayScreen to release the global gate.
        // This is needed when ackPattern is not 'Rdone' — PlayScreen's own listener
        // won't fire, so the component drives the release itself.
        onActionComplete?.()
    }, [onChange, comp.mapping, onActionComplete])

    // Listen for the ack pattern in every incoming serial message.
    useEffect(() => {
        if (!worker) return
        const handle = (e) => {
            const msg = e.data || {}
            let str = ''
            if (msg.type === 'read') str = msg.value
            else if (msg.type === 'actionRead') str = msg.value?.stringData
            else return
            if (typeof str !== 'string') return
            if (!selfLockedRef.current) return
            if (str.includes(ackPattern)) releaseSelf()
        }
        worker.addEventListener('message', handle)
        return () => worker.removeEventListener('message', handle)
    }, [worker, releaseSelf, ackPattern])

    // Unmount safety: reset ref (can't call state setter after unmount).
    useEffect(() => {
        return () => { selfLockedRef.current = false }
    }, [])

    // Stay in sync with the parent's global lock. When PlayScreen releases the
    // action gate (device "Rdone", the 5 s fallback, or any path that zeroes our
    // output state) it resets outputStates[mapping] to 0 but cannot touch our
    // local isSelfLocked. Without this, the lock lingers and this button keeps
    // showing its "pressed" colour while a newly-pressed button also lights up —
    // the "two buttons clicked at once" bug when you tap another button just as
    // the current action finishes. If the parent has cleared us, drop the
    // self-lock too so only one button is ever shown active.
    useEffect(() => {
        if (selfLockedRef.current && (outputStates[comp.mapping] || 0) === 0) {
            selfLockedRef.current = false
            setIsSelfLocked(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputStates])

    const handlePointerDown = useCallback(
        (e) => {
            if (isSelfLocked && !bypassLock) return
            if (onRequestActivate && !onRequestActivate()) return
            e.currentTarget?.setPointerCapture?.(e.pointerId)
            selfLockedRef.current = true
            setIsSelfLocked(true)
            onChange(comp.mapping, 1)
            if (liveBytesArray.length > 0 && worker && webSerial?.isConnected) {
                worker.postMessage({ type: 'writeArray', value: liveBytesArray })
            }
        },
        [isSelfLocked, bypassLock, onRequestActivate, onChange, comp.mapping, liveBytesArray, worker, webSerial]
    )

    const isBtnOn = isSelfLocked || (outputStates[comp.mapping] || 0) !== 0
    const btnImg = isBtnOn
        ? comp.properties.onImage || comp.properties.imageUrl
        : comp.properties.offImage || comp.properties.imageUrl

    return (
        <div
            id={comp.id}
            className={`absolute-component playscreen-action-button${highlightClass}`}
            style={{ ...style, zIndex: style?.zIndex ?? 20 }}
        >
            <img
                className={comp.properties.className || ''}
                src={resolveImage(btnImg)}
                alt={comp.name}
                onPointerDown={handlePointerDown}
                onDragStart={(e) => e.preventDefault()}
                style={{
                    cursor: isSelfLocked ? 'not-allowed' : 'pointer',
                    touchAction: 'none',
                }}
            />
            <span className="control-label">
                {comp.properties.label || comp.name}
            </span>
        </div>
    )
}

export default LiveAckButtonControl
